import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, getTrackingParams } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const LOCATIONS = ["Sarjapur Road", "Whitefield", "Electronic City", "Bannerghatta Road", "Varthur", "Hoskote", "North Bangalore", "Other"];
const TYPES = ["Apartment", "Villa", "Plot", "Investment Property"];
const BHKS = ["1 BHK", "2 BHK", "3 BHK", "4 BHK", "Plots"];
const BUDGETS = ["Under ₹50 Lakh", "₹50L - ₹1 Cr", "₹1 Cr - ₹2 Cr", "₹2 Cr - ₹5 Cr", "Above ₹5 Cr"];
const TIMELINES = ["Immediately", "1-3 months", "3-6 months", "6-12 months", "Just exploring"];

export default function LeadForm({
  variant = "full", leadType = "enquiry", projectName = "", sourcePage = "",
  onSuccess, compact = false, redirectThankYou = true, title, className = "",
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    full_name: "", mobile: "", email: "", preferred_location: "", property_type: "",
    bhk: "", budget: "", timeline: "", message: "", consent: true,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!/^(\+91[\-\s]?)?[6-9]\d{9}$/.test(form.mobile.trim())) {
      toast.error("Please enter a valid Indian mobile number (10 digits).");
      return;
    }
    if (!form.consent) { toast.error("Please accept the consent to proceed."); return; }
    setLoading(true);
    try {
      const payload = { ...form, lead_type: leadType, project_name: projectName, source_page: sourcePage || window.location.pathname, ...getTrackingParams() };
      const { data } = await api.post("/leads", payload);
      toast.success(data.message || "Thank you! We'll contact you shortly.");
      setDone(true);
      if (onSuccess) onSuccess(data);
      else if (redirectThankYou) setTimeout(() => navigate("/thank-you"), 900);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done && !onSuccess) {
    return (
      <div className="text-center py-8" data-testid="lead-success">
        <CheckCircle2 className="w-14 h-14 text-emerald-brand mx-auto mb-4" />
        <h3 className="font-display text-2xl text-navy mb-2">Thank You!</h3>
        <p className="text-muted-foreground">Our property advisor will contact you shortly.</p>
      </div>
    );
  }

  const isQuick = variant === "quick";

  return (
    <form onSubmit={submit} className={`space-y-4 ${className}`} data-testid="lead-form">
      {title && <h3 className="font-display text-xl text-navy">{title}</h3>}
      <div className={`grid gap-4 ${isQuick ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
        <div>
          <Label htmlFor="lf-name" className="text-xs font-semibold text-navy">Full Name *</Label>
          <Input id="lf-name" data-testid="lead-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Your name" required className="bg-gray-50 mt-1" />
        </div>
        <div>
          <Label htmlFor="lf-mobile" className="text-xs font-semibold text-navy">Mobile Number *</Label>
          <Input id="lf-mobile" data-testid="lead-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="10-digit mobile" required className="bg-gray-50 mt-1" />
        </div>
      </div>

      {variant !== "gated" && (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
          <div>
            <Label htmlFor="lf-email" className="text-xs font-semibold text-navy">Email</Label>
            <Input id="lf-email" data-testid="lead-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" className="bg-gray-50 mt-1" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-navy">Preferred Location</Label>
            <Select value={form.preferred_location} onValueChange={(v) => set("preferred_location", v)}>
              <SelectTrigger data-testid="lead-location" className="bg-gray-50 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      )}

      {variant === "full" && (
        <>
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
            <div>
              <Label className="text-xs font-semibold text-navy">Property Type</Label>
              <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                <SelectTrigger data-testid="lead-type" className="bg-gray-50 mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-navy">BHK</Label>
              <Select value={form.bhk} onValueChange={(v) => set("bhk", v)}>
                <SelectTrigger data-testid="lead-bhk" className="bg-gray-50 mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>{BHKS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-navy">Budget</Label>
              <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
                <SelectTrigger data-testid="lead-budget" className="bg-gray-50 mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>{BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-navy">Purchase Timeline</Label>
            <Select value={form.timeline} onValueChange={(v) => set("timeline", v)}>
              <SelectTrigger data-testid="lead-timeline" className="bg-gray-50 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{TIMELINES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="lf-msg" className="text-xs font-semibold text-navy">Message</Label>
            <Textarea id="lf-msg" data-testid="lead-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us your requirements..." className="bg-gray-50 mt-1" rows={3} />
          </div>
        </>
      )}

      {isQuick && (
        <div>
          <Label className="text-xs font-semibold text-navy">Budget</Label>
          <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
            <SelectTrigger data-testid="lead-budget" className="bg-gray-50 mt-1"><SelectValue placeholder="Select budget" /></SelectTrigger>
            <SelectContent>{BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      {variant === "gated" && (
        <div>
          <Label htmlFor="lf-email2" className="text-xs font-semibold text-navy">Email *</Label>
          <Input id="lf-email2" data-testid="lead-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" required className="bg-gray-50 mt-1" />
        </div>
      )}

      <div className="flex items-start gap-2 pt-1">
        <Checkbox id="lf-consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} data-testid="lead-consent" className="mt-0.5" />
        <Label htmlFor="lf-consent" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
          I agree to be contacted by Mathura Homes via phone, WhatsApp or email regarding my enquiry.
        </Label>
      </div>

      <Button type="submit" disabled={loading} data-testid="lead-submit" className="w-full bg-gold text-navy hover:bg-gold-dark font-semibold shadow-gold active:scale-95 transition-transform">
        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : (leadType === "site_visit" ? "Book Site Visit" : leadType === "callback" ? "Request Callback" : leadType === "brochure" ? "Get Access" : "Submit Enquiry")}
      </Button>
    </form>
  );
}
